'use client';

import * as React from 'react';
import { cn } from '../lib/utils.js';

export interface DatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  className?: string;
  id?: string;
  name?: string;
  error?: boolean;
}

function toISODateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      minDate,
      maxDate,
      disabled = false,
      className,
      id,
      name,
      error = false,
    },
    ref,
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      if (!raw) {
        onChange(null);
        return;
      }
      // Parse YYYY-MM-DD from native input
      const parsed = new Date(raw + 'T00:00:00');
      if (isNaN(parsed.getTime())) {
        onChange(null);
        return;
      }
      onChange(parsed);
    };

    return (
      <input
        ref={ref}
        id={id}
        name={name}
        type="date"
        value={value ? toISODateString(value) : ''}
        onChange={handleChange}
        min={minDate ? toISODateString(minDate) : undefined}
        max={maxDate ? toISODateString(maxDate) : undefined}
        disabled={disabled}
        className={cn(
          'flex h-9 w-full rounded-md border bg-white px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
          error
            ? 'border-danger focus-visible:ring-danger'
            : 'border-gray-300',
          className,
        )}
      />
    );
  },
);
DatePicker.displayName = 'DatePicker';

export { DatePicker };
